import { useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { ItemTypes } from '../constant/constants';
import { useAppDispatch } from './useAppDispatch';
import { moveColumn } from '../store/slice/column-slice';
import { store } from '../store/store';

export type ColumnDragItem = {
  column: any;
};

/**
 * useColumnDragDrop
 *
 * A custom React hook that enables drag-and-drop interaction for table columns.
 * It integrates with `react-dnd` and Redux to support reordering of *unlocked*
 * columns inside a table header.
 *
 * ### Core Responsibilities:
 * - Makes a column **draggable** (via `useDrag`)
 * - Makes the same element a **drop target** (via `useDrop`)
 * - Determines the `from` → `to` index movement based only on **unlocked columns**
 * - Dispatches `moveColumn` to update the Redux store
 * - Returns a `ref` that must be attached to the column DOM element
 *
 * ### Why this hook exists
 * This hook abstracts all drag-and-drop logic so the column header component can stay clean.
 * It ensures that:
 * - Locked columns cannot be reordered
 * - Drag and drop connectors are safely attached
 * - Redux state updates correctly and consistently
 *
 * @param column - The column object associated with the rendered header cell.
 *
 * @returns An object containing:
 * - `ref`: A React ref to attach to the column DOM element (enables drag + drop)
 * - `isDragging`: Boolean indicating whether the column is currently being dragged
 */
export function useColumnDragDrop({ column }: { column: any }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();

  /**
   * --- DROP ---
   *
   * useDrop registers this column as a *drop target*.
   * When another column is dropped onto it, we:
   *
   * 1. Read the current Redux store
   * 2. Extract only unlocked columns (locked ones cannot move)
   * 3. Determine:
   *    - `from`: index of the dragged column
   *    - `to`: index of the column being dropped onto
   * 4. Dispatch `moveColumn` only if indices are valid and different
   */
  const [, drop] = useDrop<ColumnDragItem>({
    accept: ItemTypes.COLUMN,
    drop(item) {
      // Get unlocked columns from store
      const state = store.getState();
      const unlocked = state.columnOrder.columns.filter((c) => !c.isLocked);

      const from = unlocked.findIndex((c) => c.name === item.column.name);
      const to = unlocked.findIndex((c) => c.name === column.name);

      // No movement if invalid or same position
      if (from === -1 || to === -1 || from === to) return;

      dispatch(moveColumn({ from, to }));
    },
  });

  /**
   * --- DRAG ---
   *
   * useDrag makes this column draggable.
   *
   * - Sends `{ column }` as the drag payload
   * - Tracks `isDragging` for styling or opacity effects
   * - Provides `drag` and `preview` connectors
   */
  const [{ isDragging }, drag, preview] = useDrag({
    type: ItemTypes.COLUMN,
    item: { column },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  /**
   * --- CONNECT ---
   *
   * Combines drag, drop, and preview onto the same DOM element.
   * This ensures:
   * - The element can *both* be dragged *and* receive drops
   * - Drag preview is anchored to the real element
   */
  useEffect(() => {
    if (ref.current) {
      drag(drop(ref.current)); // enable drag + drop
      preview(ref.current); // enable drag preview
    }
  }, [drag, drop, preview]);

  return { ref, isDragging };
}
